import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DesktopLayout from "@/components/Layout/DesktopLayout";
import { api, formatApiError } from "@/api/client";
import { Phone, Mail, MapPin, FileText, Wrench } from "lucide-react";

export default function CustomerDetail() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/customers/${id}`); setC(data); }
      catch (e) { setErr(formatApiError(e)); }
    })();
  }, [id]);

  if (err) return <DesktopLayout><div className="text-red-600">{err}</div></DesktopLayout>;
  if (!c) return <DesktopLayout><div className="text-slate-500">Loading…</div></DesktopLayout>;

  return (
    <DesktopLayout>
      <Link to="/customers" className="hk-tiny-label text-orange-600 hover:text-orange-700">← All customers</Link>
      <div className="flex items-end justify-between mb-6 mt-2 gap-4 flex-wrap">
        <div>
          <h1 className="text-4xl font-black tracking-tighter" style={{ fontFamily: "Chivo" }}>{c.name}</h1>
          <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-600">
            {c.phone && <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" />{c.phone}</span>}
            {c.email && <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />{c.email}</span>}
            {c.location && <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />{c.location}</span>}
          </div>
        </div>
        <Link
          to={`/quotes/new?customer_id=${c.id}`}
          className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-5 py-2.5 rounded-sm"
          data-testid="customer-new-quote"
        >
          <FileText className="w-4 h-4" /> New quote
        </Link>
      </div>

      {c.notes && (
        <div className="hk-card p-4 mb-6">
          <div className="hk-tiny-label flex items-center gap-1"><FileText className="w-3 h-3" /> Notes</div>
          <p className="text-sm mt-1 whitespace-pre-wrap">{c.notes}</p>
        </div>
      )}

      <h2 className="text-xl font-bold mb-3 flex items-center gap-2" style={{ fontFamily: "Chivo" }}>
        <span className="inline-block w-1 h-5 rounded-sm bg-gradient-to-b from-orange-500 to-rose-500" />
        Projects <span className="text-xs text-slate-500 font-normal">({c.projects_total || 0})</span>
      </h2>
      {(c.projects || []).length === 0 ? (
        <div className="hk-card p-6 text-center text-sm text-slate-500" data-testid="customer-projects-empty">
          No projects linked to this customer yet.
        </div>
      ) : (
        <div className="hk-card overflow-x-auto mb-6" data-testid="customer-projects-list">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 hk-tiny-label">
              <tr>
                <th className="px-3 py-2 text-left">Project</th>
                <th className="px-3 py-2 text-left">Stage / Status</th>
                <th className="px-3 py-2 text-left">Delivery</th>
                <th className="px-3 py-2 text-left">Created</th>
              </tr>
            </thead>
            <tbody>
              {c.projects.map((p) => (
                <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2">
                    <Link to={`/projects/${p.id}`} className="text-orange-700 hover:text-orange-900 font-mono text-xs font-bold">
                      {p.project_id}
                    </Link>
                    <div className="text-[11px] text-slate-500 truncate max-w-[240px]">{p.name || "—"}</div>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-700">{p.workflow_status || p.stage}</td>
                  <td className="px-3 py-2 text-xs text-slate-700">{p.estimated_delivery_date || "—"}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{(p.created_at || "").slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h2 className="text-xl font-bold mb-3 flex items-center gap-2" style={{ fontFamily: "Chivo" }}>
        <span className="inline-block w-1 h-5 rounded-sm bg-gradient-to-b from-sky-500 to-indigo-500" />
        Quotes <span className="text-xs text-slate-500 font-normal">({c.quotes_total || 0})</span>
      </h2>
      {(c.quotes || []).length === 0 ? (
        <div className="hk-card p-6 text-center text-sm text-slate-500" data-testid="customer-quotes-empty">
          No quotes for this customer yet.
        </div>
      ) : (
        <div className="hk-card overflow-x-auto mb-6" data-testid="customer-quotes-list">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 hk-tiny-label">
              <tr>
                <th className="px-3 py-2 text-left">Quote #</th>
                <th className="px-3 py-2 text-left">Subject</th>
                <th className="px-3 py-2 text-right">Total</th>
                <th className="px-3 py-2 text-left">State</th>
                <th className="px-3 py-2 text-left">Created</th>
              </tr>
            </thead>
            <tbody>
              {c.quotes.map((q) => (
                <tr key={q.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2">
                    <Link to={`/quotes/${q.id}`} className="text-orange-700 hover:text-orange-900 font-mono text-xs font-bold">
                      {q.quote_number}{q.version ? ` v${q.version}` : ""}
                    </Link>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-700 truncate max-w-[280px]">{q.subject || "—"}</td>
                  <td className="px-3 py-2 text-xs text-slate-800 text-right font-semibold">
                    ₹{Number(q.grand_total || 0).toLocaleString("en-IN")}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-full ${q.converted_to_project_id ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                      {q.converted_to_project_id ? "Converted" : "Open"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-500">{(q.created_at || "").slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h2 className="text-xl font-bold mb-3 flex items-center gap-2" style={{ fontFamily: "Chivo" }}>
        <span className="inline-block w-1 h-5 rounded-sm bg-gradient-to-b from-emerald-500 to-teal-500" />
        Activity <span className="text-xs text-slate-500 font-normal">({(c.activity || []).length})</span>
      </h2>
      {(c.activity || []).length === 0 ? (
        <div className="hk-card p-6 text-center text-sm text-slate-500" data-testid="customer-activity-empty">
          No activity recorded yet.
        </div>
      ) : (
        <div className="hk-card p-4" data-testid="customer-activity-list">
          <ol className="space-y-2 text-sm">
            {c.activity.map((ev, i) => (
              <li key={i} className="flex items-start gap-2 border-b border-slate-100 last:border-0 pb-2 last:pb-0">
                <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-full mt-0.5 ${ev.kind === "quote" ? "bg-sky-50 text-sky-700" : "bg-orange-50 text-orange-700"}`}>
                  {ev.kind}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-slate-800">
                    <b className="font-semibold">{ev.actor_name || "System"}</b>
                    <span className="text-slate-500"> · {ev.action || "—"}</span>
                    {ev.ref_label && (
                      <Link to={ev.kind === "quote" ? `/quotes/${ev.ref_id}` : `/projects/${ev.ref_id}`}
                        className="ml-1 text-orange-700 hover:text-orange-900 font-mono text-[11px]">
                        {ev.ref_label}
                      </Link>
                    )}
                  </div>
                  {ev.note && <div className="text-[11px] text-slate-500 mt-0.5">{ev.note}</div>}
                  <div className="text-[10px] text-slate-400 mt-0.5">{(ev.at || "").replace("T", " ").slice(0, 16)}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}
    </DesktopLayout>
  );
}
